// ---------------------------------------------------------------------------
// RoadPickerDialog — "Which road do you mean?"
//
// The confirmation step for a road contribution the resolver would not choose
// on its own. This is deliberately NOT framed as an error: for a contributor
// standing in a city canyon, or in a car park beside the road they just drove,
// confirming the road is the normal way to tag. There is no error styling, no
// apology, and no "we couldn't…" copy.
//
// The one hard rule: every row here came from roadResolve. The picker cannot
// invent a road, and dismissing it never files the contribution at the user's
// own coordinate — "None of these" cancels outright.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import type { AmbiguityReason, RoadCandidate } from "@/utils/roadResolve";
import { requestMapHighlight, clearMapHighlight } from "@/utils/mapHighlight";
import { fmtDistance } from "@/utils/units";
import { tapFeedbackClass, triggerTapFeedback } from "@/utils/tapFeedback";

interface Props {
  open: boolean;
  candidates: RoadCandidate[];
  /** Why the resolver is asking. Shapes the subtitle only — never an error. */
  reason: AmbiguityReason;
  /** Chosen road. The caller stores candidate.representative, never the fix. */
  onConfirm: (candidate: RoadCandidate) => void;
  /** Cancels the contribution. Nothing is saved. */
  onCancel: () => void;
}

/** A sentence that tells the truth about why we're asking, without blaming
 *  the user or their phone. */
function subtitleFor(reason: AmbiguityReason, count: number): string {
  if (reason === "separation") {
    return count > 2
      ? "Several roads run close together here."
      : "Two roads run close together here.";
  }
  if (reason === "distance") return "Nothing is close enough to be sure.";
  return "Your location isn't precise enough to be sure.";
}

/** Human-readable road class: "living_street" → "living street". */
const prettyClass = (highway: string) => highway.replace(/_/g, " ");

export const RoadPickerDialog = ({ open, candidates, reason, onConfirm, onCancel }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Show the road on the map as the user moves through the list — the choice
  // should be visual, not a guess from a name.
  const highlight = useCallback((c: RoadCandidate | null) => {
    setActiveId(c?.groupId ?? null);
    if (!c) { clearMapHighlight(); return; }
    requestMapHighlight({
      coords: c.geometry,
      label: c.displayName,
      ttlMs: 15_000,
      fit: false,
    });
  }, []);

  // Preview the top candidate as soon as the sheet opens, and never leave a
  // highlight behind when it closes.
  useEffect(() => {
    if (!open) { clearMapHighlight(); setActiveId(null); return; }
    if (candidates.length > 0) highlight(candidates[0]);
    return () => { clearMapHighlight(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates]);

  const choose = (c: RoadCandidate) => {
    triggerTapFeedback();
    clearMapHighlight();
    onConfirm(c);
  };

  const cancel = () => {
    triggerTapFeedback();
    clearMapHighlight();
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
      <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 space-y-1 text-left">
          <DialogTitle className="text-lg">Which road do you mean?</DialogTitle>
          <DialogDescription className="text-sm">
            {subtitleFor(reason, candidates.length)}
          </DialogDescription>
        </DialogHeader>

        <ul className="border-t border-border">
          {candidates.map((c) => {
            const active = c.groupId === activeId;
            return (
              <li key={c.groupId}>
                <button
                  type="button"
                  onClick={() => choose(c)}
                  onMouseEnter={() => highlight(c)}
                  onFocus={() => highlight(c)}
                  aria-label={`${c.displayName}, ${prettyClass(c.highway)}, ${fmtDistance(c.distM)} away`}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left border-b border-border
                    transition-smooth hover:bg-muted/50 focus-visible:bg-muted/50
                    focus-visible:outline-none ${tapFeedbackClass}`}
                >
                  <span
                    aria-hidden="true"
                    className={`w-1 self-stretch rounded-full flex-none ${
                      active ? "bg-pleasant" : "bg-border"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-sm truncate">{c.displayName}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {prettyClass(c.highway)}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums flex-none">
                    {fmtDistance(c.distM)}
                  </span>
                  {active && <Check className="w-4 h-4 text-pleasant flex-none" aria-hidden="true" />}
                </button>
              </li>
            );
          })}

          {/* Never a fallback to the contributor's own coordinate. */}
          <li>
            <button
              type="button"
              onClick={cancel}
              className={`w-full flex items-center gap-3 px-5 py-3 text-left
                transition-smooth hover:bg-muted/50 focus-visible:bg-muted/50
                focus-visible:outline-none ${tapFeedbackClass}`}
            >
              <span aria-hidden="true" className="w-1 self-stretch rounded-full flex-none bg-border" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-sm">None of these</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Don't save this rating
                </span>
              </span>
              <X className="w-4 h-4 text-muted-foreground flex-none" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  );
};
